import { useEffect, useRef, useState } from "react";
import CharacterSelection, { getRandomCharacters } from "./characterSelection/characterSelection";
import { chasers } from "../data/character";
import MapViewer from "./map/mapViewer";
import './game.css'
import StartGame from "./start/startGame";
import { useGame } from "../data/gameContext"; // Adjust the import path as needed
import GameHeader from "./header/gameHeader";
import GamePaused from "./paused/gamePaused";
import { MoveChasers, CheckAddChaser, AddChaser, SetChaserVisibility } from "./chasers/chaserAI";
import { gameConfig } from "./gameConfig";
import BasePanel from "./basePanel/basePanel";
import GameOver from "./gameOver/gameOver";
import useFullscreenHandler from "../hooks/useFullScreenHandler";
import useTravelMovement from "../hooks/useTravelMovement";
import useChaserDetection from "../hooks/useChaserDetection";
import useMapGeneration from "../hooks/useMapGeneration";
import bugBustInstance from "../debugger/bugBustLog";
import TownView from "./town/town";
import Saloon from "./town/buildings/saloon";
import GeneralStore from "./town/buildings/generalStore";
import TelegraphOffice from "./town/buildings/telegraphOffice";
import Jail from "./town/buildings/jail";
import Encounter from "./encounters/firstEncounter/encounter";


export default function Game() {
  const { gameState, updateGameState } = useGame(); // Global game state (GameContext file)
  const [prevPhase, setPrevPhase] = useState(gameState.Phase); // previous phase to resume when unpausing
  const gamePhaseRef = useRef(gameState.Phase);
  const gameHeader: boolean = gameState.Phase !== "startGame" && gameState.Phase !== "characterSelection" && gameState.Phase !== "paused";
  useEffect(() => {
    gamePhaseRef.current = gameState.Phase;
  }, [gameState.Phase]);

  

  //do things on a time basis when the player does something to advance time
  useEffect(() => {
    if(gameState.Phase == "travelling" && gameState.PlayerCharacter && gameState.PlayerCharacter.TravelTimeLeft !== undefined && gameState.PlayerCharacter.TravelTimeLeft > 1) return;
    
    //FIXME: @waynerockett Don't call this when reloading! Or on startup, so when gameTime === 0
    let newState = structuredClone(gameState)

    //Chasers
    //move all existing chasers first  
    newState.Chasers = MoveChasers(newState);

    //see if we need to add another (we need some way of getting rid of them too)
    if (CheckAddChaser(newState, gameConfig.ChaserAddInterval))
    {
      newState.Chasers = AddChaser(newState);
      newState.LastChaser = newState.GameTime;
    }

    //set visibility
    newState.Chasers = SetChaserVisibility(newState, true); //woukld be nice if I could tell if the overlay was on from here...

    //save any changes
    updateGameState(newState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameState.GameTime]

    //check if we have won or lost the game now - check victory first or loss??
  )


  async function enterFullscreen(doNotUpdate?: 'continue' | 'loaded') {
    if (window.screen.width < 1920 || window.screen.height < 1080) {
      alert("Your screen is too small! This game requires a 1920x1080 resolution.");
      return;
    }
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      await elem.requestFullscreen();
    } else if ((elem as any).webkitRequestFullscreen) {
      await (elem as any).webkitRequestFullscreen();
    } else if ((elem as any).mozRequestFullScreen) {
      await (elem as any).mozRequestFullScreen();
    } else if ((elem as any).msRequestFullscreen) {
      await (elem as any).msRequestFullscreen();
    }
    // If we're starting the game, move to character selection,
    // otherwise resume from the previous phase.
    if (!doNotUpdate){
      updateGameState({ Phase: gameState.Phase === "startGame" ? "characterSelection" : prevPhase });
    } else {
      if(doNotUpdate === 'continue') {
        updateGameState({ Phase: prevPhase });
      }
    }
  }

  //#region Game hooks
  useMapGeneration();
  useChaserDetection();
  useTravelMovement();
  useFullscreenHandler(() => {
    setPrevPhase(gamePhaseRef.current);
    updateGameState({ Phase: "paused" });
  });
  //#endregion

  //#region Render game
  return (
    <div
      className={
        gameState.Phase === "startGame"
          ? "start-game"
          : gameState.Phase === "paused"
          ? "paused"
          : "game-container"
      }
    >
      {(gameState.Phase === "startGame") && <StartGame loadGame={() => enterFullscreen('loaded')} startGame={enterFullscreen} />}
      {gameState.Phase === "characterSelection" && gameState.GameTowns.length > 0 && (
        <CharacterSelection
          characterOptions={gameState.PlayerCharacterOptions}
          playerSelectedCharacter={(c, i) =>
            updateGameState({ PlayerCharacter: { ...c, Location: {...gameState.GameTowns[i].Location}, Money: 100, Inventory: [{Id: 4, Quantity: 1}], 
              CurrentCosmeticHP: 100, MaximumCosmeticHP: 100, Stats: {Reflexes:100, Speed:100, Strength:100, Toughness: 100, Weight:100}},  
              Phase: "mapGeneration", StartingTownIndex: i }) //need a function to generate the initial state of the player after selection, for money. inventory, hp & stats.
          }
        />
      )}
      {gameHeader && <GameHeader />}
      {(gameState.Phase === "mapView" || gameState.Phase === 'travelling') &&
        gameState.GameMap &&
        gameState.GameTowns &&
        gameState.PlayerCharacter && (
          <MapViewer
            map={gameState.GameMap}
            towns={gameState.GameTowns}
            playerCharacter={gameState.PlayerCharacter}
            chasers={chasers}
            width={gameConfig.MapWidth}
            height={gameConfig.MapHeight}
            tileSize={gameConfig.TileSize}
          />
      )}
      {/* TOWN VIEWS */}
      {gameState.Phase === "townView" && <TownView />}
      {gameState.Phase === "Saloon" && <Saloon />}
      {gameState.Phase === "Jail" && <Jail />}
      {gameState.Phase === "General Store" && <GeneralStore />}
      {gameState.Phase === "Telegraph Office" && <TelegraphOffice />}
      {/* encounter views */}
      {gameState.Phase === "encounter" && <Encounter/>}
      {(gameState.Phase === 'mapView' || gameState.Phase === 'travelling' || gameState.Phase === 'townView' || gameState.Phase === 'encounter') &&
        <BasePanel />
      }
      {gameState.Phase === "paused" && <GamePaused resumeGame={() => enterFullscreen('continue')} prevPhase={prevPhase}/>}
      {gameState.Phase === "gameOver" && <GameOver />}
    </div>
  );
}
